import { createContext, useContext, type Dispatch, type SetStateAction } from "react";

import type { ChatCitation, ProposedCall, ProposedChange, Project } from "../api/types";

export interface Hit {
  project: Project;
  /** Project details is routed on a role id, so a hit keeps one to open. */
  roleId: string;
}

export type Answer =
  | { kind: "projects"; text: string; hits: Hit[] }
  /**
   * A call it has understood and not written.
   *
   * This used to be "logged", and the difference is the point. Describing a
   * call wrote the row there and then, which is the one thing in this panel
   * that cannot be taken back: it attaches to a production and tells everyone
   * following it. The model path was already built to ask first, and there is
   * no argument for the answer being safer when a model is configured and not
   * when it is.
   */
  | { kind: "offer"; text: string; proposed: ProposedCall }
  /**
   * A change to the record it has read out of pasted details and not written.
   *
   * The same shape of answer as an offer, for the grid-updates flow: a new
   * production or an amendment to one we hold, shown in full before anybody
   * agrees to it, because a confirmed change writes to a production and tells
   * everyone following it.
   */
  | { kind: "change"; text: string; change: ProposedChange }
  | { kind: "none"; text: string }
  /**
   * An answer from the model, which is a different shape from the ones above.
   *
   * The others are built here, so the panel already knows what they are about.
   * This one arrives as a sentence with a list of the productions it was built
   * from, because nothing on this side decided what it says. Those rows are
   * the only way a reader can check it, which matters more here than anywhere
   * else in the app: the data comes out of agency grids people already go back
   * to the PDF to verify.
   */
  | {
      kind: "model";
      text: string;
      citations: ChatCitation[];
      /** A call it would write, waiting on somebody to say yes. */
      proposed: ProposedCall | null;
    };

export type Turn = { question: string; answer: Answer | null };

export interface Assistant {
  /** Every production it can answer about. */
  hits: Hit[];
  /** True while the productions are being read. */
  loading: boolean;
  /** True when the last attempt to read them did not finish. */
  failed: boolean;
  /** Read them if they are not already read. Safe to call on every render. */
  ensure: () => void;
  /**
   * Whether there is a model behind the panel.
   *
   * null until it has been asked. The panel says nothing about being connected
   * or not while it does not know, rather than guessing at the answer people
   * will read off the badge.
   */
  connected: boolean | null;
  /** The conversation so far, oldest first. */
  turns: Turn[];
  setTurns: Dispatch<SetStateAction<Turn[]>>;
}

export const AssistantContext = createContext<Assistant | null>(null);

export function useAssistant(): Assistant {
  const value = useContext(AssistantContext);
  if (value === null) {
    throw new Error("useAssistant needs an AssistantProvider above it.");
  }
  return value;
}
